import { QuickLoginModalHOC } from '@/components/modal/LoginModal2HOC';
import useDebounce from '@/hooks/useDebounce';
import { useScreen } from '@/hooks/useScreen';
import { EPostVoteType } from '@/interfaces/post.interface';
import { putVotePost } from 'apis/tms/post.api';
import { convertQuantity } from 'common/quantityHelpers';
import { PATH_NAME } from 'configs/pathName';
import { PostContext } from 'context/PostContext';
import Button from 'design-system/components/Button/Button';
import {
  ButtonEmphasis,
  ButtonSize,
} from 'design-system/components/Button/types';
import dynamic from 'next/dynamic';
import { useRouter } from 'next/router';
import { useContext, useEffect, useRef, useState } from 'react';
import { useMutation } from 'react-query';
import FloatingPostMenuHelper from './FloatingPostMenuHelper';

const Tooltips = dynamic(
  () => import('design-system/components/Tooltips/Tooltips')
);

export default function PostReactButton() {
  const { group, isLocked, ...postInfo } = useContext(PostContext);
  const [isReact, setIsReact] = useState<boolean>(postInfo.isReact);
  const router = useRouter();

  const { isDesktop } = useScreen();

  const prevReact = useRef(postInfo.isReact);

  const debounceValue = useDebounce({ value: isReact, delay: 500 });

  const [reactCount, setReactCount] = useState<number>(postInfo.react);

  useEffect(() => {
    setReactCount(postInfo.react);
    setIsReact(postInfo.isReact);
    prevReact.current = postInfo.isReact;
  }, [postInfo.isReact, postInfo.react]);

  const { mutate } = useMutation(putVotePost, {
    onSuccess() {
      prevReact.current = isReact;
    },
  });

  const onClickReact = () => {
    setIsReact((react) => {
      return !react;
    });
    if (isReact) {
      setReactCount((prev) => (prev > 0 ? prev - 1 : 0));
      return;
    }
    setReactCount((prev) => prev + 1);
  };

  useEffect(() => {
    if (debounceValue !== prevReact.current) {
      if (isLocked) {
        router.push({
          pathname: PATH_NAME.MY_GROUPS_DETAIL,
          query: {
            gid: group?.groupId,
          },
        });
        return;
      }

      mutate({ postId: postInfo.postId, type: EPostVoteType.Love });
      prevReact.current = isReact;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debounceValue]);

  const buttonProps: {
    size: ButtonSize;
    emphasis: ButtonEmphasis;
    className: string;
  } = {
    size: 'medium',
    emphasis: 'medium',
    className: `${isReact && 'text-Other/HeartLiked'}`,
  };

  return (
    <QuickLoginModalHOC
      classname="cursor-pointer"
      handleClickWhenDisabledQuickLogin={onClickReact}
      loginSuccessCb={() => {
        onClickReact();
        if (router.basePath !== PATH_NAME.HOME_FEED_POST_DETAIL) {
          router.push({
            pathname: PATH_NAME.HOME_FEED_POST_DETAIL,
            query: {
              pid: postInfo.postId,
            },
          });
        }
      }}
    >
      {isDesktop ? (
        <>
          <Tooltips title={'Yêu thích'}>
            <Button
              {...buttonProps}
              mode="icon-only"
              icon={{
                name: 'heart',
                variant: !isReact ? 'outline' : 'solid',
              }}
            />
          </Tooltips>
          <FloatingPostMenuHelper>
            {convertQuantity(reactCount)}
          </FloatingPostMenuHelper>
        </>
      ) : (
        <Button
          {...buttonProps}
          mode="text-only"
          startIcon={{
            name: 'heart',
            variant: !isReact ? 'outline' : 'solid',
          }}
        >
          <span className="text-Primary/Main">
            {convertQuantity(reactCount)}
          </span>
        </Button>
      )}
    </QuickLoginModalHOC>
  );
}
